export interface RunGroup<T> {
  run: T;
  /** Older runs, directly after this one, that failed on the same headline — folded into this (newest) one. */
  repeats: T[];
}

/**
 * Folds back-to-back failures with the same headline into the newest of them
 * ("same error ×N"). `runs` is newest first; `failureKey` is the failure
 * headline, or null for a run that didn't fail. Any run that didn't fail, or
 * failed on something else, ends the streak, so a failure is never folded into
 * one from before a successful run and every group keeps its place in time.
 */
export function groupRepeatedFailures<T>(runs: readonly T[], failureKey: (run: T) => string | null): RunGroup<T>[] {
  const groups: RunGroup<T>[] = [];
  let streak: { key: string; group: RunGroup<T> } | null = null;
  for (const run of runs) {
    const key = failureKey(run);
    if (key !== null && streak && streak.key === key) {
      streak.group.repeats.push(run);
      continue;
    }
    const group: RunGroup<T> = { run, repeats: [] };
    groups.push(group);
    streak = key === null ? null : { key, group };
  }
  return groups;
}

/** Dates a "same error ×N · also …" line names before it counts the rest. */
export const REPEAT_DATES_SHOWN = 3;

/** "12 Oct 2026 05:16, 11 Oct 2026 13:16, 11 Oct 2026 05:15 and 43 more"; every date stays in the tooltip. */
export function repeatDates(dates: readonly string[], shown = REPEAT_DATES_SHOWN): string {
  if (dates.length <= shown + 1) return dates.join(', ');
  return `${dates.slice(0, shown).join(', ')} and ${dates.length - shown} more`;
}

/** The fields of a history run the ordering and labels below read. */
export interface ShownRun {
  state: string;
  createdOn: string;
  startedOn: string | null;
  completedOn: string | null;
  error?: string | null;
}

/**
 * The time a history row shows, and sorts by. A finished run shows when it
 * ended, a run still going when it started. A run a restart lost shows when it
 * was due or began (`slotOf` maps a never-started run to its scheduled slot),
 * never when the restart reaped it hours later: that time sat a 08:15 run above
 * the 10:19 one that replaced it.
 */
export function runShownAt(
  run: ShownRun,
  casualty: 'missed' | 'interrupted' | null,
  slotOf?: (startedAt: Date) => Date | null,
): string {
  const start = run.startedOn ?? run.createdOn;
  if (casualty === 'missed') return slotOf?.(new Date(start))?.toISOString() ?? start;
  if (casualty === 'interrupted') return start;
  // A cancelled run shows when it started, like the list's "Last:"; the cancel time is a detail.
  if (run.state === 'cancelled') return start;
  return run.completedOn ?? start;
}

/** Newest first by the time each row shows, so the list reads in the order of its own dates. */
export function sortByShownTime<T>(runs: readonly T[], shownAt: (run: T) => string): T[] {
  return runs
    .map((run, i) => ({ run, i, t: new Date(shownAt(run)).getTime() }))
    .sort((a, b) => b.t - a.t || a.i - b.i)
    .map((x) => x.run);
}

/** A job failing this many runs in a row will not heal itself: it needs someone. */
export const NEEDS_FIX_STREAK = 3;

/**
 * Under a failing row, what happens next, so Walter can tell "wait" from "act":
 *   - "Needs a fix" when no deploy can close it (a missing key, setting, credit
 *     or access: `configGap`), or when it has failed NEEDS_FIX_STREAK runs in a
 *     row (Weekly Revenue Insights, 7 weeks of different Anthropic errors);
 *   - "Next run … is the first on the new build" when it last failed before the
 *     running build went live and has not run since: that run will tell. It
 *     never claims a fix is live: a deploy is not a fix for this job;
 *   - otherwise just when it tries again.
 */
export function nextRunNote(
  failedAt: string,
  configGap: boolean,
  deployedAt: string | null,
  when: string,
  streak = 1,
): string {
  if (configGap || streak >= NEEDS_FIX_STREAK) return `Needs a fix: next run ${when}`;
  if (deployedAt && new Date(failedAt).getTime() < new Date(deployedAt).getTime()) return `Next run ${when} is the first on the new build`;
  return `Next run ${when}`;
}

const brusselsDayKey = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels' });
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "3 Oct" from a "2026-10-03" day key (fixed month names, never ICU's "Sept"). */
function dayLabel(key: string): string {
  const [, m, d] = key.split('-').map(Number) as [number, number, number];
  return `${d} ${MONTHS[m - 1]}`;
}

/** True for a cron that fires once every day ("15 8 * * *"). */
export function firesOncePerDay(cron: string): boolean {
  const f = cron.trim().split(/\s+/);
  return f.length === 5 && /^\d+$/.test(f[0]!) && /^\d+$/.test(f[1]!) && f[2] === '*' && f[3] === '*' && f[4] === '*';
}

/**
 * The Brussels days strictly between two shown times with no run at all, as a
 * quiet note: "No run on 3 Oct", "No runs 3–5 Oct", "No runs 3 Aug – 20 Sep".
 * Null when the two runs sit on the same or neighbouring days.
 */
export function noRunNote(newer: string, older: string): string | null {
  const DAY = 86_400_000;
  const a = brusselsDayKey.format(new Date(newer));
  const b = brusselsDayKey.format(new Date(older));
  const gap = Math.round((Date.parse(a) - Date.parse(b)) / DAY) - 1;
  if (gap < 1) return null;
  const first = brusselsDayKey.format(new Date(Date.parse(b) + DAY + 12 * 3_600_000));
  const last = brusselsDayKey.format(new Date(Date.parse(a) - DAY + 12 * 3_600_000));
  if (gap === 1) return `No run on ${dayLabel(first)}`;
  const [f, l] = [dayLabel(first), dayLabel(last)];
  const sameMonth = first.slice(0, 7) === last.slice(0, 7);
  return sameMonth ? `No runs ${f.split(' ')[0]}–${l}` : `No runs ${f} – ${l}`;
}

/** A run left "running" longer than this is Stuck: the process running it died. Same rule as the API's status. */
export const STUCK_AFTER_MS = 60 * 60 * 1000;

/** A heartbeat older than this means the run's process is gone. Same as the API's engine-liveness DEAD_AFTER_MS. */
export const DEAD_AFTER_MS = 5 * 60 * 1000;

/**
 * Whether the run's own liveness stamp (`output.liveness`, written by the
 * engine every minute while it runs) says its process is alive: a fresh
 * heartbeat, or parked on a delay step. Null when the run carries no stamp.
 */
export function runIsAlive(output: unknown, now = Date.now()): boolean | null {
  const l = (output as { liveness?: { heartbeatAt?: unknown; parked?: unknown } } | null)?.liveness;
  if (!l || typeof l.heartbeatAt !== 'string') return null;
  if (l.parked === true) return true;
  const beat = Date.parse(l.heartbeatAt);
  return Number.isFinite(beat) && now - beat <= DEAD_AFTER_MS;
}

/**
 * An unfinished run (running, queued) nothing will finish. With a liveness
 * stamp, its heartbeat decides; without one (older runs), the hour rule the
 * API also uses.
 */
export function isStuckRun(
  run: Pick<ShownRun, 'state' | 'createdOn' | 'startedOn' | 'completedOn'> & { output?: unknown },
  now = Date.now(),
): boolean {
  const unfinished = ['active', 'running', 'created', 'pending'].includes(run.state) && !run.completedOn;
  if (!unfinished) return false;
  const alive = runIsAlive(run.output, now);
  if (alive !== null) return !alive;
  return now - new Date(run.startedOn ?? run.createdOn).getTime() > STUCK_AFTER_MS;
}

/**
 * One row per schedule slot for the runs a restart lost. When the slot's work
 * completed after all (a re-run, a re-queue), its missed and interrupted rows
 * go: the slot is done, so they would only say it twice. When it did not, one
 * restart row stays (the interrupted one, which actually ran, over a queue
 * miss). Runs are newest first; `slotKey` is null for runs with no slot, which
 * are always kept.
 */
export function oneRowPerSlot<T>(
  runs: readonly T[],
  slotKey: (run: T) => string | null,
  casualty: (run: T) => 'missed' | 'interrupted' | null,
  completed: (run: T) => boolean,
): T[] {
  const done = new Set<string>();
  const kept = new Map<string, T>();
  for (const r of runs) {
    const k = slotKey(r);
    if (k !== null && completed(r)) done.add(k);
  }
  for (const r of runs) {
    const k = slotKey(r);
    const c = casualty(r);
    if (k === null || !c || done.has(k)) continue;
    const prev = kept.get(k);
    if (!prev || (c === 'interrupted' && casualty(prev) !== 'interrupted')) kept.set(k, r);
  }
  return runs.filter((r) => {
    const k = slotKey(r);
    if (k === null || !casualty(r)) return true;
    return !done.has(k) && kept.get(k) === r;
  });
}
